<template>
  <div class="table-buttons">
    <button @click="insertTable" title="Insert Table">
      <img width="20" src="/images/tiptap-bar/table.svg" alt="Table" />
    </button>
    <template v-if="editor.isActive('table')">
      <button
        @click="editor.chain().focus().addColumnBefore().run()"
        title="Add Column Before"
      >
        <img
          width="20"
          src="/images/tiptap-bar/column-before.svg"
          alt="Column Before"
        />
      </button>
      <button
        @click="editor.chain().focus().addColumnAfter().run()"
        title="Add Column After"
      >
        <img
          width="20"
          src="/images/tiptap-bar/column-after.svg"
          alt="Column After"
        />
      </button>
      <button
        @click="editor.chain().focus().deleteColumn().run()"
        title="Delete Column"
      >
        <img
          width="20"
          src="/images/tiptap-bar/delete-column.svg"
          alt="Delete Column"
        />
      </button>
      <button
        @click="editor.chain().focus().addRowBefore().run()"
        title="Add Row Before"
      >
        <img
          width="20"
          src="/images/tiptap-bar/row-before.svg"
          alt="Row Before"
        />
      </button>
      <button
        @click="editor.chain().focus().addRowAfter().run()"
        title="Add Row After"
      >
        <img
          width="20"
          src="/images/tiptap-bar/row-after.svg"
          alt="Row After"
        />
      </button>
      <button
        @click="editor.chain().focus().deleteRow().run()"
        title="Delete Row"
      >
        <img
          width="20"
          src="/images/tiptap-bar/delete-row.svg"
          alt="Delete Row"
        />
      </button>
      <button
        @click="editor.chain().focus().deleteTable().run()"
        title="Delete Table"
      >
        <img
          width="20"
          src="/images/tiptap-bar/delete-table.svg"
          alt="Delete Table"
        />
      </button>
      <button
        @click="editor.chain().focus().mergeOrSplit().run()"
        title="Merge/Split Cells"
      >
        <img
          width="20"
          src="/images/tiptap-bar/merge-cells.svg"
          alt="Merge/Split Cells"
        />
      </button>

      <!-- Toggle Table Border -->
      <button
        @click="toggleTableBorder"
        :class="{ 'is-active': isTableBordered }"
        title="Toggle Table Border"
      >
        <img
          width="20"
          src="/images/tiptap-bar/table-border.svg"
          alt="Table Border"
        />
      </button>

      <!-- Toggle Header Row -->
      <button
        @click="editor.chain().focus().toggleHeaderRow().run()"
        :class="{ 'is-active': editor.isActive('tableHeader') }"
        title="Toggle Header Row"
      >
        <img
          width="20"
          src="/images/tiptap-bar/header-row.svg"
          alt="Header Row"
        />
      </button>

      <!-- Toggle Header Column -->
      <button
        @click="editor.chain().focus().toggleHeaderColumn().run()"
        :class="{ 'is-active': editor.isActive({ headerColumn: true }) }"
        title="Toggle Header Column"
      >
        <img
          width="20"
          src="/images/tiptap-bar/header-column.svg"
          alt="Header Column"
        />
      </button>

      <!-- Cell Background Color -->
      <button @click="openCellBgColor" title="Cell Background Color">
        <img
          width="20"
          src="/images/tiptap-bar/cell-bg.svg"
          alt="Cell Background"
        />
      </button>

      <!-- Cell Text Alignment -->
      <button
        @click="editor.chain().focus().setCellTextAlign('left').run()"
        :class="{ 'is-active': isCellAligned('left') }"
        title="Align Cell Content Left"
      >
        <img
          width="20"
          src="/images/tiptap-bar/cell-align-left.svg"
          alt="Cell Align Left"
        />
      </button>

      <button
        @click="editor.chain().focus().setCellTextAlign('center').run()"
        :class="{ 'is-active': isCellAligned('center') }"
        title="Align Cell Content Center"
      >
        <img
          width="20"
          src="/images/tiptap-bar/cell-align-center.svg"
          alt="Cell Align Center"
        />
      </button>

      <button
        @click="editor.chain().focus().setCellTextAlign('right').run()"
        :class="{ 'is-active': isCellAligned('right') }"
        title="Align Cell Content Right"
      >
        <img
          width="20"
          src="/images/tiptap-bar/cell-align-right.svg"
          alt="Cell Align Right"
        />
      </button>
    </template>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["open-cell-bg-color-modal"]);

const isTableBordered = ref(true);

// Cell text alignment functions
const isCellAligned = (alignment) => {
  if (
    !(
      props.editor.isActive("tableCell") || props.editor.isActive("tableHeader")
    )
  ) {
    return false;
  }

  const cellAttributes = props.editor.isActive("tableCell")
    ? props.editor.getAttributes("tableCell")
    : props.editor.getAttributes("tableHeader");

  const styleString = cellAttributes.style || "";
  return styleString.includes(`text-align: ${alignment}`);
};

// Cell background color function
const openCellBgColor = () => {
  emit("open-cell-bg-color-modal");
};

// Table border toggle function
const toggleTableBorder = () => {
  const dom = props.editor.view.dom;
  const table =
    dom.querySelector(".ProseMirror-selectednode") ||
    dom.querySelector("table");

  if (table) {
    // Get the current state of the table
    const isBordered = !table.classList.contains("borderless-table");

    // Update the table class
    if (isBordered) {
      table.classList.remove("default-table");
      table.classList.add("borderless-table");
      isTableBordered.value = false;
    } else {
      table.classList.remove("borderless-table");
      table.classList.add("default-table");
      isTableBordered.value = true;
    }

    // Store in editor
    const attributes = {
      class: isTableBordered.value ? "default-table" : "borderless-table",
    };

    props.editor.chain().focus().updateAttributes("table", attributes).run();

    // Force a re-render of the table
    props.editor.view.dispatch(
      props.editor.state.tr.setMeta("forceUpdate", true)
    );
  }
};

// Table insertion with default class
const insertTable = () => {
  props.editor
    .chain()
    .focus()
    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
    .run();

  // Ensure the class is added after table insertion
  setTimeout(() => {
    const dom = props.editor.view.dom;
    const table = dom.querySelector("table:not(.default-table)");
    if (table) {
      table.classList.add("default-table");
      isTableBordered.value = true;

      // Update the attributes in the editor state
      props.editor
        .chain()
        .focus()
        .updateAttributes("table", {
          class: "default-table",
        })
        .run();
    }
  }, 10);
};
</script>

<style scoped>
.table-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
